import { TextComponent } from '@manifest-cyber/design-system/lib/components/TextComponent';
import { ManifestApplicationSettings } from '@manifest-cyber/types/interface/appSettings';
import { InterfaceOrganization } from '@manifest-cyber/types/interface/dbTables';
import { JobEventPayload } from '@manifest-cyber/types/interface/job';
import { Button, Stack } from '@mantine/core';
import { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Table } from 'rsuite';
import { AxiosProxy } from '../../api/axiosProxy/axiosProxy';
import { OrganizationApi } from '../../api/organization/organization.api';
import AppSettingsModal from '../../components/AppSettingsModal';
import ClickableRegion from '../../components/ClickableRegion';
import { Dropdown } from '../../components/Dropdown/Dropdown';
import Icon from '../../components/Icon';
import ManifestModal, {
  ManifestModalProps,
} from '../../components/ManifestModal/ManifestModal';
import TriggerMessageModal from '../../components/TriggerMessageModal';
import { TriggerType } from '../../components/TriggerMessageModal/TriggerMessageModal';
import { useAuth } from '../../hooks/useAuth';
import { ManifestNotifications } from '../../hooks/utils/useNotifications';
import { useOrganizationId } from '../../hooks/utils/useOrganizationId';
import representOrganization from '../../lib/representOrganization';
import { captureExceptionWithMessage } from '../../lib/sentry/captureExceptionWithMessage/captureExceptionWithMessage';
import '../../scss/pages/administration.scss';
import { OrganizationSubscriptionModal } from './components/OrganizationSubscriptionModal';
import { usePutSyncRolesPermissions } from './hooks/query/usePutSyncRolesPermissions';
const { Column, HeaderCell, Cell } = Table;

interface EndpointConfig {
  label: string;
  modal: Pick<ManifestModalProps, 'title' | 'children' | 'primaryAction'>;
}

const getJobs = (t: TFunction): { label: string; value: TriggerType }[] => {
  return [
    {
      label: t('page.administration.daily-vuln-match'),
      value: 'daily-vuln-match',
    },
    {
      label: t('page.administration.seed-nvd'),
      value: 'nvd-ingest',
    },
    {
      label: t('page.administration.seed-osv'),
      value: 'osv-ingest',
    },
    {
      label: t('page.administration.seed-epss'),
      value: 'epss-ingest',
    },
    {
      label: t('page.administration.seed-kev'),
      value: 'kev-ingest',
    },
  ];
};

const Administration = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [currentOrgId, setCurrentOrgId] = useOrganizationId();
  const [newOrgForm, setNewOrgForm] = useState<any | null>(null);
  const [isCreatingNewOrg, setIsCreatingNewOrg] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [isFetchingOrgs, setIsFetchingOrgs] = useState(false);
  const [fetchedOrgs, setFetchedOrgs] = useState<InterfaceOrganization[]>([]);
  const [sortColumn, setSortColumn] = useState();
  const [sortType, setSortType] = useState();
  const [selectedOrg, setSelectedOrg] = useState<null | InterfaceOrganization>(null);
  const [appSettings, setAppSettings] = useState<ManifestApplicationSettings | null>(
    null,
  );
  const [appSettingLoading, setAppSettingsLoading] = useState<boolean>(false);
  const jobs = getJobs(t);
  const {
    mutateAsync: putSyncRolesPermissions,
    isLoading: isSyncRolesPermissionsLoading,
  } = usePutSyncRolesPermissions();

  const endpoints: EndpointConfig[] = [
    {
      label: t('page.administration.triggerEndpointMenu.syncRolesPermissions'),
      modal: {
        title: t('page.administration.triggerEndpointModal.title'),
        children: (
          <Stack spacing={8}>
            <TextComponent>
              {t('page.administration.syncRolesPermissions.warning')}
            </TextComponent>
            <TextComponent>
              {t('page.administration.syncRolesPermissions.usage')}
            </TextComponent>
            <TextComponent>
              {t('page.administration.syncRolesPermissions.asyncOperation')}
            </TextComponent>
          </Stack>
        ),
        primaryAction: {
          label: t('page.administration.triggerEndpointModal.confirm'),
          onClick: async () => {
            try {
              await putSyncRolesPermissions();
              ManifestNotifications.showSuccessNotification({
                title: t('global.success'),
                message: t('page.administration.syncRolesPermissions.successMessage'),
              });
              setEndpointModalContent(undefined);
            } catch (error) {
              captureExceptionWithMessage('Unable to sync roles permissions!', error);
              ManifestNotifications.showErrorNotification({
                title: t('global.error'),
                message: t('global.unexpectedErrorSubtitle'),
              });
            }
          },
        },
      },
    },
  ];

  const getOrgData = () => {
    if (sortColumn && sortType) {
      return fetchedOrgs.sort((a, b) => {
        let x: any =
          a[sortColumn] === undefined ? Number.NEGATIVE_INFINITY : a[sortColumn];
        let y: any =
          b[sortColumn] === undefined ? Number.NEGATIVE_INFINITY : b[sortColumn];

        if (sortType === 'asc') {
          if (x < y) return -1;
          if (x > y) return 1;
          return 0;
        } else {
          if (x < y) return 1;
          if (x > y) return -1;
          return 0;
        }
      });
    }
    return fetchedOrgs;
  };

  const handleSortColumn = (sortColumn: any, sortType: any) => {
    setIsFetchingOrgs(true);
    setTimeout(() => {
      setIsFetchingOrgs(false);
      setSortColumn(sortColumn);
      setSortType(sortType);
    }, 500);
  };

  const createNewOrg = async () => {
    setIsCreatingNewOrg(true);

    if (newOrgForm?.name && newOrgForm?.name.length > 0) {
      try {
        await OrganizationApi.createOrganization({
          body: {
            name: newOrgForm?.name,
            domains: newOrgForm?.domains || null,
            isPaid: true,
            enableInviteOnly: false,
          },
        });
      } catch (error) {
        setErrors([error as string]);
      }
    } else {
      setErrors([...errors, "Can't create an organization with no name!"]);
    }

    setIsCreatingNewOrg(false);
    await fetchOrgs();
  };

  useEffect(() => {
    setErrors([]);
  }, [newOrgForm]);

  const attemptImpersonation = async (orgId: string) => {
    window.localStorage.setItem('previousorgid', currentOrgId || '');
    await representOrganization(orgId);
    setCurrentOrgId(orgId);
  };

  const reprocessSbom = async (organizationId: string) => {
    setTriggerBody({
      organizationId,
    });
    setTriggerType('sbom-reprocess');
    setJobModalOpen(true);
  };

  const fetchOrgs = async () => {
    setIsFetchingOrgs(true);
    try {
      const orgs: {
        success: boolean;
        errors: string[];
        data: InterfaceOrganization[];
      } = await AxiosProxy.get({ url: 'admin/organizations' });

      if (orgs?.success) {
        if (Array.isArray(orgs?.data)) {
          setFetchedOrgs(orgs?.data);
        } else {
          captureExceptionWithMessage('Administration: Fetched orgs is not an array!');
        }
      } else {
        setErrors(['Unable to fetch orgs (success false)']);
        captureExceptionWithMessage('Unable to fetch orgs (success false)');
      }

      setIsFetchingOrgs(false);
    } catch (err) {
      captureExceptionWithMessage('Unable to fetch orgs!', err);
    }
  };

  const fetchAppSettings = async () => {
    try {
      const appSettings: {
        success: boolean;
        errors: string[];
        data: ManifestApplicationSettings;
      } = await AxiosProxy.get({ url: 'app-settings/' });

      if (appSettings?.success) {
        setAppSettings(appSettings.data);
      } else {
        setErrors(['Unable to fetch app settings (success false)']);
        captureExceptionWithMessage('Unable to fetch app settings (success false)');
      }
    } catch (err) {
      captureExceptionWithMessage('Unable to fetch app settings!', err);
    }
  };

  const [isJobModalOpen, setJobModalOpen] = useState(false);
  const [endpointModalContent, setEndpointModalContent] = useState<
    EndpointConfig['modal'] | undefined
  >();
  const [triggerType, setTriggerType] = useState<TriggerType | undefined>();
  const [triggerBody, setTriggerBody] = useState<JobEventPayload | undefined>();
  const [isAppSettingsModalOpen, setAppSettingsModalOpen] = useState(false);

  return (
    <section className="page-administration">
      <div className="header">
        <div className="header-grouper">
          <h1>{t('page.administration.header')}</h1>
          <span className="label-internal">{t('page.administration.internalOnly')}</span>
        </div>

        {endpointModalContent && (
          <ManifestModal
            opened
            onClose={() => setEndpointModalContent(undefined)}
            title={endpointModalContent.title}
            children={endpointModalContent.children}
            primaryAction={
              endpointModalContent.primaryAction && {
                ...endpointModalContent.primaryAction,
                isLoading: isSyncRolesPermissionsLoading,
              }
            }
            secondaryAction={{
              label: t('global.cancel'),
              onClick: () => {
                setEndpointModalContent(undefined);
              },
            }}
          />
        )}
        {triggerType && (
          <TriggerMessageModal
            open={isJobModalOpen}
            onClose={() => setJobModalOpen(false)}
            triggerType={triggerType}
            triggerBody={triggerBody || {}}
          />
        )}
        <AppSettingsModal
          open={isAppSettingsModalOpen}
          onClose={() => setAppSettingsModalOpen(false)}
          appSettings={appSettings}
        />
        <div className="action-menu">
          <Button
            className="app-settings-button"
            onClick={async () => {
              setAppSettingsLoading(true);
              await fetchAppSettings();
              setAppSettingsLoading(false);
              setAppSettingsModalOpen(true);
            }}
          >
            <Icon icon="gear" size="1x" spin={appSettingLoading}></Icon>
          </Button>

          <Dropdown targetProps={{ label: t('page.administration.trigger') }}>
            {jobs.map((job) => (
              <Dropdown.Item
                onClick={() => {
                  setTriggerType(job.value);
                  setJobModalOpen(true);
                }}
              >
                {job.label}
              </Dropdown.Item>
            ))}
          </Dropdown>

          <Dropdown
            targetProps={{ label: t('page.administration.triggerEndpointMenu.label') }}
          >
            {endpoints.map((endpoint) => (
              <Dropdown.Item
                onClick={() => {
                  setEndpointModalContent(endpoint.modal);
                }}
              >
                {endpoint.label}
              </Dropdown.Item>
            ))}
          </Dropdown>
        </div>
      </div>
      <div className="muted">{t('page.administration.subHeader')}</div>
      {errors.length > 0 && (
        <ul className="page-errors anim-slideInUpShort">
          {errors.map((error) => (
            <li>{error}</li>
          ))}
        </ul>
      )}

      <div className="create-org">
        <h4>{t('page.administration.createNewOrganization')}</h4>
        <div className="create-org-inner">
          <Form
            layout="inline"
            className="flex"
            onChange={(val: any) => setNewOrgForm(val)}
          >
            <Form.Group className="flex flex-col" controlId="username-7">
              <Form.ControlLabel className="muted">{t('global.name')}</Form.ControlLabel>
              <Form.Control name="name" placeholder={t('global.organizationName')} />
            </Form.Group>

            <Form.Group className="flex flex-col" controlId="password-7">
              <Form.ControlLabel className="muted">
                {t('global.domain')}
              </Form.ControlLabel>
              <Form.Control name="domains" autoComplete="off" placeholder="****.com" />
            </Form.Group>

            <Button
              className="btn-create-org"
              disabled={isCreatingNewOrg}
              onClick={() => createNewOrg()}
            >
              {t('global.create')}
            </Button>
          </Form>
        </div>
      </div>

      <div className="list-orgs">
        <h4>{t('global.allOrganizations')}</h4>
        <div className="display-fetched-orgs">
          <Table
            // @ts-ignore
            data={getOrgData()}
            autoHeight
            sortColumn={sortColumn}
            sortType={sortType}
            onSortColumn={handleSortColumn}
            loading={isFetchingOrgs}
            renderEmpty={() => (
              <div className="table-empty">
                <ClickableRegion
                  regionLabel="Click to load organizations"
                  onClick={() => fetchOrgs()}
                  className="fetch-table-data"
                >
                  <span>Click to Fetch Data. Requests are audited.</span>
                </ClickableRegion>
              </div>
            )}
          >
            <Column sortable flexGrow={2}>
              <HeaderCell>Name</HeaderCell>
              <Cell dataKey="name" />
            </Column>

            <Column sortable flexGrow={2}>
              <HeaderCell>Organization ID</HeaderCell>
              <Cell dataKey="_id" />
            </Column>

            <Column sortable flexGrow={1}>
              <HeaderCell>Status</HeaderCell>
              <Cell dataKey="status" />
            </Column>

            <Column sortable flexGrow={2}>
              <HeaderCell>Domains</HeaderCell>
              <Cell dataKey="domains">
                {(rowData, rowIndex) => {
                  return `${
                    rowData.domains
                      .sort((a: string, b: string) => {
                        const nameA = a.toLowerCase(),
                          nameB = b.toLowerCase();
                        if (nameA < nameB) return -1;
                        if (nameA > nameB) return 1;
                        return 0;
                      })
                      .join(',') || 'None'
                  }`;
                }}
              </Cell>
            </Column>

            <Column sortable flexGrow={2}>
              <HeaderCell>Users</HeaderCell>
              <Cell dataKey="countUsers">
                {(rowData, rowIndex) => {
                  return `${rowData.countUsers || '-'}`;
                }}
              </Cell>
            </Column>
            <Column sortable flexGrow={2}>
              <HeaderCell>Date Created</HeaderCell>
              <Cell dataKey="dateCreated">
                {(rowData, rowIndex) => {
                  return `${rowData.dateCreated || '-'}`;
                }}
              </Cell>
            </Column>

            <Column flexGrow={3}>
              <HeaderCell>{t('page.administration.actions')}</HeaderCell>
              <Cell>
                {(rowData: InterfaceOrganization, rowIndex) => {
                  if (rowData?._id === currentOrgId) {
                    return (
                      <>
                        <strong>Current Organization</strong>
                      </>
                    );
                  }

                  return (
                    <div className="flex flex-row">
                      <ClickableRegion
                        regionLabel={t('page.administration.impersonate')}
                        className="action-btn"
                        onClick={() =>
                          rowData?._id
                            ? attemptImpersonation(rowData._id.toString())
                            : null
                        }
                      >
                        <Icon icon="masks-theater" size="1x"></Icon>
                      </ClickableRegion>
                      <ClickableRegion
                        regionLabel={t('page.administration.reprocess-sboms')}
                        className="action-btn"
                        onClick={() => {
                          rowData?._id ? reprocessSbom(rowData._id.toString()) : null;
                        }}
                      >
                        <Icon icon="arrows-rotate" size="1x"></Icon>
                      </ClickableRegion>
                      <ClickableRegion
                        regionLabel="Change Subscription"
                        className="action-btn"
                        onClick={() => setSelectedOrg(rowData)}
                      >
                        <Icon icon="circle-arrow-up" size="1x"></Icon>
                      </ClickableRegion>
                    </div>
                  );
                }}
              </Cell>
            </Column>
          </Table>
        </div>

        {selectedOrg && (
          <OrganizationSubscriptionModal
            opened
            organization={{
              _id: selectedOrg._id?.toString() || '',
              name: selectedOrg.name,
              subscriptions: selectedOrg.subscriptions,
            }}
            onSuccess={fetchOrgs}
            onClose={() => setSelectedOrg(null)}
          />
        )}
      </div>
    </section>
  );
};

export default Administration;
